import {
	Button,
	HStack,
	Select,
	SelectOptionType,
} from '@montel/montelpro-shared-components-v2';
import { FunctionComponent, useState } from 'react';
import { styled } from 'styled-components';
import { useAdminUsersContext } from '..';
import { toRoleOptions } from '../toRoleOptions';
import { ADMIN_ACCOUNT_API_ROUTE } from 'apiRoutes';
import useSubmit, { POST } from 'hooks/useSubmit';
import { RequestStatus } from '@montel/montelpro-shared-components/enums';
import { LabelAndValue } from 'components/common/LabelAndValue/LabelAndValue';
import { AccessControl } from 'components/common/AccessControl/AccessControl';
import { AdminAccount } from 'domain/account/interfaces';
import { AdminRole, getAdminRoleNameFromValue } from 'domain/account/enums';

const ButtonContainer = styled(HStack)`
	height: 40px;
`;

export const Roles: FunctionComponent<{ user: AdminAccount }> = ({ user }) => {
	const [editMode, setEditMode] = useState(false);
	const [roles, setRoles] = useState(user?.roles);
	const [selectedRoles, setSelectedRoles] = useState(user?.roles);
	const [errors, setErrors] = useState({ roles: '' });
	const { adminRoles } = useAdminUsersContext();
	const [updateRoles, status] = useSubmit(
		`${ADMIN_ACCOUNT_API_ROUTE}/UpdateRoles`,
		POST,
		{
			messageOnSuccess: 'Roles updated successfully',
			messageOnError: 'Error updating roles',
			showApiError: true,
		},
	);

	const onSaveRoles = async () => {
		const response = await updateRoles({
			adminId: user?.id,
			roles: selectedRoles,
		});
		if (response.success) {
			setEditMode(false);
			setRoles(selectedRoles);
		}
	};

	const onChangeRoles = (value: SelectOptionType[]) => {
		setSelectedRoles(value.map((v) => v.value));

		if (value.length) {
			setErrors({
				roles: '',
			});
		}
	};

	const onCancel = () => {
		setSelectedRoles(roles);
		setEditMode(false);
	};

	return editMode ? (
		<HStack gap="md" wrap="nowrap" fullWidth align="flex-end">
			<Select
				label="Roles"
				options={toRoleOptions(adminRoles)}
				value={toRoleOptions(selectedRoles)}
				onChange={onChangeRoles}
				isMulti
				variant={'checkbox'}
				errorMessage={errors.roles && errors.roles}
			/>
			<AccessControl allowedRoles={[AdminRole.SUPER_ADMIN]}>
				<ButtonContainer wrap="nowrap" align="center">
					<Button
						text="Cancel"
						onClick={onCancel}
						size="sm"
						variant="secondary"
					/>
					<Button
						text="Save"
						onClick={onSaveRoles}
						size="sm"
						isLoading={status === RequestStatus.LOADING}
						loadingText="Saving..."
						isDisabled={!selectedRoles.length}
					/>
				</ButtonContainer>
			</AccessControl>
		</HStack>
	) : (
		<HStack gap="md" wrap="nowrap" align="flex-end">
			<LabelAndValue
				label="Roles"
				value={roles
					.map((r) => getAdminRoleNameFromValue(r))
					.join(', ')}
			/>
			<AccessControl allowedRoles={[AdminRole.SUPER_ADMIN]}>
				<Button
					text="Edit"
					onClick={() => setEditMode(true)}
					size="sm"
				/>
			</AccessControl>
		</HStack>
	);
};
